import get from 'lodash.get';
import { getYoutubeVideoId } from './video-url';

const AD_MARKER_MARGIN = 0.001;
const filterMarkersByType = (markers, type) => (markers || []).filter(m => m.type === type);

export const DEFAULT_THUMBNAIL = '/assets/images/default-marker.png';

export const MARKER_TYPES = {
  AD: 'Ad',
  AUDIO: 'Audio',
  IMAGE: 'Image',
  LINK: 'Link',
  VIDEO: 'Video',
};

export const getAdMarkers = markers => filterMarkersByType(markers, MARKER_TYPES.AD);

export const getAudioMarker = markers => (markers || []).find(m => m.type === MARKER_TYPES.AUDIO);

export const getAudioMarkerUrl = markers => {
  const audioMarker = getAudioMarker(markers) || {};
  return get(audioMarker, 'body.media.0.url', undefined);
};

export const hasAudioMarkerWithMedia = markers => !!getAudioMarkerUrl(markers);

export const validateMarker = (marker, episodeDuration) => {
  if (!marker) return null;

  const errors = {};
  const body = marker.body || {};

  if (!marker.type) {
    errors.type = true;
  }

  if (
    marker.type === MARKER_TYPES.AD &&
    body.placement === 'midroll' &&
    marker.start === episodeDuration / 2
  ) {
    errors.custom = 'Mid-roll position must be adjusted.';
  }

  if (
    (marker.type === MARKER_TYPES.IMAGE || marker.type === MARKER_TYPES.VIDEO) &&
    (marker.thumbnail.original === DEFAULT_THUMBNAIL || !marker.thumbnail.original)
  ) {
    errors.thumbnail = true;
  }

  if (marker.type === MARKER_TYPES.LINK && !body.linkurl) {
    errors.linkUrl = 'Cannot be blank.';
  }

  if (marker.type === MARKER_TYPES.VIDEO) {
    if (!body.videourl) {
      errors.videoUrl = 'Cannot be blank.';
    } else if (!getYoutubeVideoId(body.videourl)) {
      errors.videoUrl = 'Currently only YouTube videos are supported.';
    }
  }

  return Object.keys(errors).length ? errors : null;
};

export const getMarkerEndpoints = (markers, markerId, episodeDuration) => {
  const marker = markers.find(m => m.id === markerId);

  if (marker.type !== MARKER_TYPES.AD) {
    return [0, episodeDuration];
  }

  const adMarkers = filterMarkersByType(markers, MARKER_TYPES.AD);
  const { placement } = marker.body;

  const endpointsByType = {
    preroll: duration => {
      const midroll = adMarkers.find(m => m.body.placement === 'midroll');
      const defaultLimit = duration * 0.25;
      const midrollLimit = midroll && midroll.start - AD_MARKER_MARGIN;
      const max = midrollLimit ? Math.min(midrollLimit, defaultLimit) : defaultLimit;

      return [0, max];
    },
    midroll: duration => {
      const preroll = adMarkers.find(m => m.body.placement === 'preroll');
      const postroll = adMarkers.find(m => m.body.placement === 'postroll');
      const min = (preroll && preroll.start + AD_MARKER_MARGIN) || 0;
      const max = (postroll && postroll.start - AD_MARKER_MARGIN) || duration;

      return [min, max];
    },
    postroll: duration => {
      const midroll = adMarkers.find(m => m.body.placement === 'midroll');
      const defaultLimit = duration * 0.5;
      const midrollLimit = midroll && midroll.start + AD_MARKER_MARGIN;
      const min = midrollLimit ? Math.max(midrollLimit, defaultLimit) : defaultLimit;

      return [min, duration];
    },
  };

  return endpointsByType[placement](episodeDuration);
};
